import React, { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Header,
  Segment,
  Form,
  Dropdown,
  Button,
  Icon,
  Message,
  Table,
  Label,
  Checkbox,
  Menu
} from "semantic-ui-react";
import {
  useGetSystemsQuery,
  useGetTalkgroupsQuery,
  useGetSubsQuery,
  useAddSubMutation,
  useRemoveSubMutation
} from "../features/api/apiSlice";
import "../darkTheme.css";

// Multi-RID autocomplete backed by the tagged-radio-ID DB (/radio_ids/search),
// mirroring the RidSelect used in the advanced filter. A subscription can match
// on any of the picked radio IDs. `value` is an array of rid strings.
function RidPicker({ value, onChange }) {
  const asOption = (v, text) => ({ key: String(v), value: String(v), text: text || String(v) });
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  // Merge new options into the existing set (keyed by value) so previously
  // selected rids keep their alias text after a fresh search replaces results.
  const mergeOptions = (incoming) => {
    setOptions((prev) => {
      const byVal = new Map(prev.map((o) => [o.value, o]));
      incoming.forEach((o) => byVal.set(o.value, o));
      return Array.from(byVal.values());
    });
  };

  const runSearch = (q) => {
    const term = (q || "").trim();
    if (!term) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(process.env.REACT_APP_BACKEND_SERVER + "/radio_ids/search?q=" + encodeURIComponent(term))
      .then((r) => r.json())
      .then((data) => {
        const found = (data.results || []).map((r) =>
          asOption(r.rid, (r.alias && r.alias !== "N/A") ? `${r.alias} (${r.rid})` : String(r.rid))
        );
        mergeOptions(found);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleSearchChange = (e, { searchQuery }) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(searchQuery), 250);
  };

  return (
    <Dropdown
      placeholder="Search RID or unit alias…"
      fluid multiple selection search allowAdditions
      options={options}
      value={(value || []).map(String)}
      loading={loading}
      noResultsMessage="Type to search radio IDs"
      onSearchChange={handleSearchChange}
      onAddItem={(e, { value: v }) => mergeOptions([asOption(v)])}
      onChange={(e, { value: v }) => onChange(v)}
    />
  );
}

export default function NotificationsPage() {
  const { data: systemsData } = useGetSystemsQuery();
  const systems = (systemsData && systemsData.systems) || [];

  const [shortName, setShortName] = useState("");
  const [rids, setRids] = useState([]);
  const [talkgroupNums, setTalkgroupNums] = useState([]);
  const [bell, setBell] = useState(true);
  const [webhook, setWebhook] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");

  const { data: tgData } = useGetTalkgroupsQuery(shortName, { skip: !shortName });
  const { data: subs = [] } = useGetSubsQuery();
  const [addSub, { isLoading: adding }] = useAddSubMutation();
  const [removeSub] = useRemoveSubMutation();

  const systemOptions = useMemo(
    () => systems.map((s) => ({ key: s.shortName, value: s.shortName, text: s.name || s.shortName })),
    [systems]
  );

  const talkgroupOptions = useMemo(() => {
    const tgs = (tgData && tgData.talkgroups) || {};
    return Object.keys(tgs).map((num) => {
      const tg = tgs[num];
      // Prefer the human-readable description (properly cased) over the alpha
      // tag, matching how talkgroups are labelled elsewhere in the app.
      const name = tg.description || tg.alpha;
      return { key: num, value: Number(num), text: name ? `${name} (${num})` : String(num) };
    }).sort((a, b) => a.value - b.value);
  }, [tgData]);

  const systemName = (sn) => {
    const s = systems.find((x) => x.shortName === sn);
    return (s && s.name) || sn;
  };

  // Render a subscription's talkgroups/rids, falling back to the legacy scalar
  // field for any subscription not yet migrated to arrays.
  const subList = (arr, legacy) => {
    const vals = Array.isArray(arr) && arr.length ? arr : (legacy != null ? [legacy] : []);
    return vals.join(", ");
  };

  const reset = () => {
    setRids([]);
    setTalkgroupNums([]);
    setLabel("");
    setWebhook(false);
    setWebhookUrl("");
    setBell(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const deliveries = [];
    if (bell) deliveries.push("bell");
    if (webhook) deliveries.push("webhook");

    if (!shortName || rids.length === 0 || talkgroupNums.length === 0) {
      setError("Pick a system, at least one talkgroup and at least one radio ID.");
      return;
    }
    if (deliveries.length === 0) {
      setError("Choose at least one delivery method (bell or webhook).");
      return;
    }
    if (webhook && !/^https?:\/\//i.test(webhookUrl.trim())) {
      setError("Enter a valid http(s) webhook URL.");
      return;
    }

    try {
      await addSub({
        shortName,
        talkgroupNums: talkgroupNums.map(Number),
        rids: rids.map(Number),
        deliveries,
        webhookUrl: webhook ? webhookUrl.trim() : "",
        label: label.trim()
      }).unwrap();
      reset();
    } catch (err) {
      setError((err && err.data && err.data.message) || "Could not save subscription.");
    }
  };

  return (
    <div>
      <Menu fixed="top">
        <Link to="/"><Menu.Item><Icon name="arrow left" /> Back</Menu.Item></Link>
        <Menu.Item header><Icon name="bell" /> Notifications</Menu.Item>
      </Menu>

      <Container style={{ marginTop: "5em", marginBottom: "3em" }}>
        <Header as="h2">
          <Icon name="bell outline" />
          <Header.Content>
            Notifications
            <Header.Subheader>
              Get alerted when any of the chosen radio IDs is heard on any of the chosen
              talkgroups — in the bell or via webhook. Each subscription fires at most once
              every 10 minutes.
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Segment>
          <Header as="h4">New subscription</Header>
          <Form onSubmit={submit} error={!!error}>
            <Form.Group widths="equal">
              <Form.Field>
                <label>System</label>
                <Dropdown
                  placeholder="Select system"
                  fluid selection search
                  options={systemOptions}
                  value={shortName}
                  onChange={(e, { value }) => { setShortName(value); setTalkgroupNums([]); }}
                />
              </Form.Field>
              <Form.Field>
                <label>Talkgroups</label>
                <Dropdown
                  placeholder={shortName ? "Select talkgroups" : "Pick a system first"}
                  fluid multiple selection search
                  disabled={!shortName}
                  options={talkgroupOptions}
                  value={talkgroupNums}
                  onChange={(e, { value }) => setTalkgroupNums(value)}
                />
              </Form.Field>
              <Form.Field>
                <label>Radio IDs (units)</label>
                <RidPicker value={rids} onChange={setRids} />
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <label>Label (optional)</label>
              <input
                placeholder="e.g. Command unit on dispatch"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </Form.Field>

            <Form.Group inline>
              <label>Deliver to</label>
              <Form.Field>
                <Checkbox label="Notification bell" checked={bell} onChange={(e, d) => setBell(d.checked)} />
              </Form.Field>
              <Form.Field>
                <Checkbox label="Webhook" checked={webhook} onChange={(e, d) => setWebhook(d.checked)} />
              </Form.Field>
            </Form.Group>

            {webhook && (
              <Form.Field>
                <label>Webhook URL</label>
                <input
                  placeholder="https://example.com/hook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </Form.Field>
            )}

            <Message error content={error} />
            <Button primary type="submit" loading={adding} disabled={adding}>
              <Icon name="plus" /> Add subscription
            </Button>
          </Form>
        </Segment>

        <Header as="h4">Your subscriptions</Header>
        {subs.length === 0 ? (
          <Message info content="You have no notification subscriptions yet." />
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>System</Table.HeaderCell>
                <Table.HeaderCell>Talkgroups</Table.HeaderCell>
                <Table.HeaderCell>Radio IDs</Table.HeaderCell>
                <Table.HeaderCell>Delivery</Table.HeaderCell>
                <Table.HeaderCell>Label</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {subs.map((s) => (
                <Table.Row key={s._id}>
                  <Table.Cell>{systemName(s.shortName)}</Table.Cell>
                  <Table.Cell>{subList(s.talkgroupNums, s.talkgroupNum)}</Table.Cell>
                  <Table.Cell>{subList(s.rids, s.rid)}</Table.Cell>
                  <Table.Cell>
                    {(s.deliveries || []).map((d) => (
                      <Label key={d} size="tiny">
                        <Icon name={d === "webhook" ? "linkify" : "bell"} />
                        {d === "webhook" ? "Webhook" : "Bell"}
                      </Label>
                    ))}
                    {s.webhookUrl ? <div style={{ fontSize: "0.8em", opacity: 0.7 }}>{s.webhookUrl}</div> : null}
                  </Table.Cell>
                  <Table.Cell>{s.label}</Table.Cell>
                  <Table.Cell collapsing>
                    <Button
                      icon="trash"
                      size="tiny"
                      color="red"
                      basic
                      onClick={() => removeSub(s._id)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>
    </div>
  );
}
